import { EditBucket, GetBucket, RegisterBucket } from "@/dtos/Bucket.dto";
import { StateObject } from "@/dtos/State.dto";
import Bucket from "@/services/bucket/bucket";
import {
  BULK_DELETE_BUCKETS,
  CLEAR_BUCKET_ISLOADING,
  DELETE_BUCKET,
  EDIT_BUCKET,
  GET_BUCKET,
  GET_BUCKETS,
  GET_BUCKET_COUNTS,
  GET_BUCKET_STATS,
  REGISTER_BUCKET,
} from "@/store/action-types";
import {
  SET_BUCKET,
  SET_BUCKETS,
  SET_BUCKET_COUNTS,
  SET_BUCKET_STATS,
  SET_ERRORMSG,
} from "@/store/mutation-types";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

const bucket: Module<any, {}> = {
  state: {
    bucket: {
      data: null,
      isLoading: false,
    },
    buckets: {
      data: null,
      metaData: null,
      isLoading: false,
    },
    bucketStats: {
      data: null,
      isLoading: false,
    },
    bucketCounts: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    bucket: (state: { bucket: any }) => state.bucket,
    buckets: (state: { buckets: any }) => state.buckets,
    bucketStats: (state: { bucketStats: any }) => state.bucketStats,
    bucketCounts: (state: { bucketCounts: any }) => state.bucketCounts,
  },
  mutations: {
    [SET_BUCKET]: (
      state: { bucket: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.bucket.data = payload.data;
      state.bucket.isLoading = payload.isLoading;
    },
    [SET_BUCKETS]: (
      state: { buckets: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      if (payload.data != null) state.buckets.data = payload.data;
      if (payload.metaData != null) state.buckets.metaData = payload.metaData;
      state.buckets.isLoading = payload.isLoading;
    },
    [SET_BUCKET_STATS]: (
      state: { bucketStats: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.bucketStats.data = payload.data;
      state.bucketStats.isLoading = payload.isLoading;
    },
    [SET_BUCKET_COUNTS]: (
      state: { bucketCounts: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.bucketCounts.data = payload.data;
      state.bucketCounts.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_BUCKET]: async ({ commit }: any, payload: any) => {
      commit(SET_BUCKET, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Bucket.registerBucket(payload as RegisterBucket);

        if (result) {
          if (result.data.status) {
            commit(SET_BUCKET, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_BUCKET, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_BUCKET, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_BUCKET, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to register bucket");
        }
        return false;
      }
    },
    [GET_BUCKETS]: async ({ commit }: any, payload: any) => {
      commit(SET_BUCKETS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Bucket.getBuckets(payload as GetBucket);

        if (result) {
          if (result.data.status) {
            commit(SET_BUCKETS, {
              data: result.data.data,
              metaData: result.data.metaData,
              isLoading: false,
            });
            return true;
          }
          commit(SET_BUCKETS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_BUCKETS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_BUCKETS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load buckets");
        }
        return false;
      }
    },
    [GET_BUCKET]: async ({ commit }: any, payload: any) => {
      commit(SET_BUCKET, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Bucket.getBuckets(payload as GetBucket);

        if (result) {
          if (result.data.status) {
            commit(SET_BUCKET, { data: result.data.data[0], isLoading: false });
            return true;
          }
          commit(SET_BUCKET, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_BUCKET, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_BUCKET, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load bucket");
        }
        return false;
      }
    },
    [GET_BUCKET_STATS]: async ({ commit }: any, payload: any) => {
      commit(SET_BUCKET_STATS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Bucket.getBucketStats(payload as GetBucket);

        if (result) {
          if (result.data.status) {
            commit(SET_BUCKET_STATS, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_BUCKET_STATS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_BUCKET_STATS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_BUCKET_STATS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load bucket stats");
        }
        return false;
      }
    },
    [GET_BUCKET_COUNTS]: async ({ commit }: any, payload: any) => {
      commit(SET_BUCKET_COUNTS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Bucket.getBucketCounts(payload as GetBucket);

        if (result) {
          if (result.data.status) {
            commit(SET_BUCKET_COUNTS, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_BUCKET_COUNTS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_BUCKET_COUNTS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_BUCKET_COUNTS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load bucket counts");
        }
        return false;
      }
    },
    [EDIT_BUCKET]: async ({ commit }: any, payload: any) => {
      commit(SET_BUCKET, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Bucket.editBucket(payload as EditBucket);

        if (result) {
          if (result.data.status) {
            commit(SET_BUCKET, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_BUCKET, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_BUCKET, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_BUCKET, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to edit bucket");
        }
        return false;
      }
    },
    [DELETE_BUCKET]: async ({ commit }: any, payload: string) => {
      commit(SET_BUCKET, { data: { id: payload }, isLoading: true });

      try {
        const result: AxiosResponse = await Bucket.deleteBucket(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_BUCKET, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_BUCKET, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_BUCKET, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_BUCKET, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to delete bucket");
        }
        return false;
      }
    },
    [BULK_DELETE_BUCKETS]: async ({ commit }: any, payload: string[]) => {
      commit(SET_BUCKET, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Bucket.bulkDeleteBuckets(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_BUCKET, { data: result.data.data, isLoading: false });
            return result.data.data; // { deletedCount }
          }
          commit(SET_BUCKET, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg || result.data.message);
          return false;
        } else {
          commit(SET_BUCKET, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_BUCKET, { data: null, isLoading: false });
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to bulk delete buckets");
        }
        return false;
      }
    },
    [CLEAR_BUCKET_ISLOADING]: ({ commit }) => {
      commit(SET_BUCKET, { isLoading: false });
      commit(SET_BUCKETS, { isLoading: false });
    },
  },
};

export default bucket;
